import { FunctionComponent, useEffect, useState } from "react";
import Table from "../../Components/Table/Table";
import CustomToolbar from "../../Components/Table/components/CustomToolbar";
import ButtonField from "../../Components/Button/ButtonField";
import { Columns } from "../../Components/Table/ITable";
import { GridRenderCellParams } from "@mui/x-data-grid";
import IconButtonField from "../../Components/Button/IconButtonField";
import { SvgIcon } from "@mui/material";
import { ReactComponent as Delete } from "../../Assets/Images/delete.svg";
import { ReactComponent as Edit } from "../../Assets/Images/edit.svg";
import { ReactComponent as RefreshButton } from "../../Assets/Images/RefreshButton.svg";
import DeleteDialog from "../../Components/DeleteDialog/DeleteDialog";
import TableSearch from "../../Components/Table/components/TableSearch";
import Dialog from "../../Components/Dialog/Dialog";
import { Box, TextField, FormControlLabel, Switch, Select, MenuItem, InputLabel, FormControl } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { useNavigate } from "react-router-dom";
import { listCourseMaster } from "../../Redux/Slices/CourseMaster/ListCourseMasterSlice";
import { addCourseMaster, resetaddCourseMaster } from "../../Redux/Slices/CourseMaster/AddCourseMasterSlice";
import {  listCourseCategory } from "../../Redux/Slices/CourseCategory/ListCourseCategorySlice";
import { ICourseMaster } from "./ICourseMaster";
import ResponseDialog from "../../Components/ResponseDialog/ResponseDialog";

interface CourseMasterProps {}

const CourseMaster: FunctionComponent<CourseMasterProps> = () => {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deleteDialogData, setDeleteData] = useState<ICourseMaster>({});
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [formData, setFormData] = useState<ICourseMaster>({
    name: "",
    full_name: "",
    short_name: "",
    course_code: "",
    course_category_id: "",
    description: "",
    status: true,
    is_delete: false
  });

  const dispatch = useAppDispatch();
  const listDataResult = useAppSelector((state) => state.ListCourseMasterSlice || { data: null });
  const categoryListResult = useAppSelector((state) => state.ListCourseCategorySlice || { data: null });
  const addResponseResult = useAppSelector((state) => state.AddCourseMasterSlice || { data: null });

  const { data: listData, isLoading } = listDataResult;
  const { data: categoryList } = categoryListResult;
  const { data: addResponse } = addResponseResult;


  const courses = Array.isArray(listData?.data)
    ? (listData?.data || []).map((c: any, index: number) => ({ 
        ...c, 
        index: index + 1,
        course_category_name: c?.course_category?.name || ""
      }))
    : [];

  const categories = Array.isArray(categoryList?.data) ? (categoryList?.data || []) : [];

  useEffect(() => {
    dispatch(listCourseMaster());
    dispatch(listCourseCategory());
  }, [dispatch]);

  // Refresh list on success of add, edit, delete (all use addCourseMaster)
  useEffect(() => {
    if (addResponse?.status === true) {
      dispatch(listCourseMaster());
    }
  }, [addResponse, dispatch]);

  // Clean up responses on unmount
  useEffect(() => {
    return () => {
      dispatch(resetaddCourseMaster());
    };
  }, [dispatch]);

  const handleOpenDialog = (data?: any) => {
    if (data) {
      setIsEdit(true);
      setFormData({
        id: data.id,
        full_name: data.full_name || data.name || "",
        short_name: data.short_name || "",
        course_code: data.course_code || "",
        course_category_id: data.course_category?.course_category_id || "",
        description: data.description || "",
        status: data.status,
        is_delete: data.is_delete
      });
    } else {
      setIsEdit(false);
      setFormData({
        full_name: "",
        short_name: "",
        course_code: "",
        course_category_id: "",
        description: "",
        status: true,
        is_delete: false
      });
    }
    setDialogOpen(true);
  };

  const handleSave = () => {
    // Use addCourseMaster for both add and edit operations
    // API will determine based on presence of id (add = no id, edit = has id)
    dispatch(addCourseMaster(formData));
  };

  const handleDelete = () => {
    if (deleteDialogData) {
      const deletePayload = {
        ...deleteDialogData,
        course_category_id: deleteDialogData?.course_category?.course_category_id || deleteDialogData?.course_category_id || "",
        is_delete: true
      };
      dispatch(addCourseMaster(deletePayload));
    }
    setDeleteDialogOpen(false);
  };

  const columns: Columns = [
    {
      field: "index",
      renderHeader: () => <b>S.No</b>,
      width: 90,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "full_name",
      renderHeader: () => <b>Course Name</b>,
      flex: 1,
      type: "string",
      headerAlign: "left",
    },
    {
      field: "course_category_name",
      renderHeader: () => <b>Category Name</b>,
      flex: 1,
      type: "string",
      headerAlign: "left",
    },
    {
      field: "description",
      renderHeader: () => <b>Description</b>,
      flex: 1.5,
      type: "string",
      headerAlign: "left",
    },
    {
      field: "status",
      renderHeader: () => <b>Status</b>,
      width: 120,
      type: "string",
      headerAlign: "left",
      cellClassName: (params) =>
        `activename ${params.row.status ? "active" : "inactive"}`,
      valueFormatter: (params) =>
        params.value === true ? "Active" : "Inactive",
    },
    {
      field: "action",
      renderHeader: () => <b>Action</b>,
      width: 150,
      type: "actions",
      renderCell: (params: GridRenderCellParams<any>) => {
        return (
          <>
            <IconButtonField
              onClick={() => {
                navigate(`/online-tutoring/courses/create?id=${params.row.id}&course_id=${params.row.course_id || ''}`);
              }}
            >
              <SvgIcon sx={{ fill: "none" }} component={Edit} inheritViewBox />
            </IconButtonField>
            <IconButtonField
              onClick={() => {
                setDeleteData({ ...params.row, id: params.id });
                setDeleteDialogOpen(true);
              }}
            >
              <SvgIcon component={Delete} inheritViewBox />
            </IconButtonField>
          </>
        );
      },
    },
  ];

  return (
    <>
      <Table rows={courses} columns={columns} exportFileName="CourseMaster" >
        <CustomToolbar>
          <TableSearch placeholder="Search Course..." />
          <Box sx={{ flexGrow: 1 }} />
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <ButtonField
              className="tabel_add_new_button"
              onClick={() => navigate('/online-tutoring/courses/create')}
              toolTip="Add new course"
            >
              Add new
            </ButtonField>
            <IconButtonField
              className="refresh_btn"
              toolTip="Refresh"
              onClick={() => dispatch(listCourseMaster())}
            >
              <SvgIcon
                className="refresh_svg_icon"
                component={RefreshButton}
                inheritViewBox
              />
            </IconButtonField>
          </div>
        </CustomToolbar>
      </Table>

      <Dialog 
        open={dialogOpen} 
        setOpen={setDialogOpen}
        maxWidth="sm" 
        fullWidth
        Dialog_Title={`${isEdit ? "Edit" : "Add"} Course`}
        Dialog_Content={
            <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2, pb: 2 }}>
            <TextField
                label="Full Name"
                value={formData.full_name || ""}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                fullWidth
                required
                variant="outlined"
            />
            <TextField
                label="Short Name"
                value={formData.short_name || ""}
                onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                fullWidth
                required
                variant="outlined"
            />
            <TextField
                label="Course Code"
                value={formData.course_code}
                onChange={(e) => setFormData({ ...formData, course_code: e.target.value })}
                fullWidth
                required
                variant="outlined"
            />
            <FormControl fullWidth required>
              <InputLabel id="course-category-label">Course Category</InputLabel>
              <Select
                labelId="course-category-label"
                value={formData.course_category_id || ""}
                label="Course Category"
                onChange={(e) => setFormData({ ...formData, course_category_id: e.target.value as string })}
              >
                {categories.map((category: any) => (
                  <MenuItem key={category.id} value={category.course_category_id || ""}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={4}
                variant="outlined"
            />
            <FormControlLabel
                control={
                <Switch
                    checked={formData.status ?? true}
                    onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                    color="primary"
                />
                }
                label={formData.status ? "Status: Active" : "Status: Inactive"}
            />
            </Box>
        }
        Dialog_Actions={
          <>
            <ButtonField 
              className="form_cancel_button" 
              onClick={() => setDialogOpen(false)}
            >
              Cancel
            </ButtonField>
            <ButtonField 
              className="form_save_button" 
              onClick={handleSave}
            >
              {isEdit ? "Update" : "Save"}
            </ButtonField>
          </>
        }
      />

      <ResponseDialog response={addResponse} parentDialog={setDialogOpen} />

      <DeleteDialog
        deleteableName={deleteDialogData.full_name ?? deleteDialogData.name ?? ""}
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        deleteFunction={handleDelete}
      />
    </>
  );
};

export default CourseMaster;
